/**
 * AuthService - Headless authentication logic for metalark
 *
 * Pure authentication logic with NO UI dependencies.
 * Uses existing axios API pattern with cookie-based auth.
 *
 * This separation allows the design team to work on UI independently
 * without touching authentication logic.
 */

import { api } from '../api/api.js';
import { ReturningUserManager } from './returning-user-manager.js';

// Lazy import to allow proper code splitting
let sessionRenewalServiceInstance = null
async function getSessionRenewalService() {
    if (!sessionRenewalServiceInstance) {
        const { sessionRenewalService } = await import('./sessionRenewalService.js')
        sessionRenewalServiceInstance = sessionRenewalService
    }
    return sessionRenewalServiceInstance
}

/**
 * Authentication service for metalark
 * Handles password, passkey, and OAuth authentication
 */
export class AuthService {
  /**
   * Cache for in-flight getCurrentUser requests
   * Prevents duplicate /auth/me requests when called simultaneously
   * @private
   */
  static _getCurrentUserPromise = null;

  /**
   * Login with username/password
   * Backend handles Cognito authentication and sets httpOnly cookies
   * 
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  static async loginWithPassword(email, password) {
    try {
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      const userData = response.data;
      
      // Save returning user info only if user has at least one auth method configured
      if (userData.hasPasskey || userData.hasPassword) {
        ReturningUserManager.saveUser({
          userUuid: userData.userUuid,
          email: userData.email,
          name: userData.name || userData.email,
          hasPasskey: userData.hasPasskey,
          hasPassword: userData.hasPassword
        });
      }
      
      return {
        success: true,
        user: userData
      };
    } catch (error) {
      console.error('Password login failed:', error);
      
      // Preserve error code for special handling (e.g., EULA required)
      const errorCode = error.response?.data?.error;
      const errorData = error.response?.data;
      
      return {
        success: false,
        error: this.formatError(error),
        errorCode: errorCode,
        errorData: errorData
      };
    }
  }

  /**
   * Begin passkey authentication flow
   * Gets authentication options from backend
   * 
   * @param {string} email - User's email (optional for discoverable credentials)
   * @returns {Promise<{success: boolean, options?: object, error?: string}>}
   */
  static async beginPasskeyLogin(email = null) {
    try {
      // Request WebAuthn challenge from backend
      const response = await api.post('/auth/passkey/login/challenge', {
        email
      });
      
      return {
        success: true,
        options: response.data
      };
    } catch (error) {
      console.error('Failed to get passkey challenge:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Complete passkey authentication
   * Uses browser's WebAuthn API and verifies with backend
   *
   * @param {string|null} email - User's email (optional for discoverable credentials)
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  static async loginWithPasskey(email = null) {
    try {
      // Check if passkeys are supported
      if (!this.isPasskeySupported()) {
        const { locale } = await import('./localeService.js');
        return {
          success: false,
          error: locale.t('passkey_notsupported')
        };
      }

      // Get authentication challenge from backend
      // When email is null, backend will return challenge for discoverable credentials (empty allowCredentials)
      const challengeResult = await this.beginPasskeyLogin(email);
      if (!challengeResult.success) {
        return challengeResult;
      }

      // Convert base64 challenge options to ArrayBuffers for WebAuthn API
      const publicKeyOptions = this.preparePublicKeyOptions(challengeResult.options);

      // Use browser's WebAuthn API to get credential
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions
      });

      if (!credential) {
        const { locale } = await import('./localeService.js');
        return {
          success: false,
          error: locale.t('passkey_error_no_selection')
        };
      }

      // Prepare verification payload
      const verifyPayload = {
        credential: {
          id: credential.id,
          rawId: this.arrayBufferToBase64(credential.rawId),
          response: {
            authenticatorData: this.arrayBufferToBase64(credential.response.authenticatorData),
            clientDataJSON: this.arrayBufferToBase64(credential.response.clientDataJSON),
            signature: this.arrayBufferToBase64(credential.response.signature),
            userHandle: credential.response.userHandle ? this.arrayBufferToBase64(credential.response.userHandle) : null
          },
          type: credential.type
        }
      };

      // Only include email if provided (for non-discoverable flow)
      if (email) {
        verifyPayload.email = email;
      }

      // Send credential to backend for verification
      const response = await api.post('/auth/passkey/login/verify', verifyPayload);

      // API returns {success: true, user: {...}}
      const result = response.data;
      const userData = result.user;
      
      // Save returning user info only if user has at least one auth method configured
      if (userData.hasPasskey || userData.hasPassword) {
        ReturningUserManager.updateUser({
          userUuid: userData.userUuid,
          email: userData.email,
          name: userData.name || userData.email,
          hasPasskey: userData.hasPasskey,
          hasPassword: userData.hasPassword
        });
      }
      
      return {
        success: true,
        user: userData
      };
    } catch (error) {
      console.error('Passkey login failed:', error);
      
      // Preserve error code for special handling (e.g., EULA required)
      const errorCode = error.response?.data?.error;
      const errorData = error.response?.data;
      
      return {
        success: false,
        error: this.formatError(error),
        errorCode: errorCode,
        errorData: errorData
      };
    }
  }

  /**
   * Initiate OAuth login (Google)
   * Redirects to OAuth initiate endpoint
   */
  static async loginWithGoogle() {
    try {
      // Get OAuth initiation URL from backend
      const response = await api.get('/auth/oauth/google/initiate', {
        params: {
          callback_uri: `${window.location.origin}/auth/callback`
        }
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.authorizationUrl;
      
      return { success: true };
    } catch (error) {
      console.error('Google login initiation failed:', error);
      return { 
        success: false, 
        error: this.formatError(error)
      };
    }
  }

  /**
   * Initiate OAuth login (Microsoft)
   * Redirects to OAuth initiate endpoint
   */
  static async loginWithMicrosoft() {
    try {
      // Get OAuth initiation URL from backend
      const response = await api.get('/auth/oauth/microsoft/initiate', {
        params: {
          callback_uri: `${window.location.origin}/auth/callback`
        }
      });
      
      // Redirect to Microsoft OAuth
      window.location.href = response.data.authorizationUrl;
      
      return { success: true };
    } catch (error) {
      console.error('Microsoft login initiation failed:', error);
      return { 
        success: false, 
        error: this.formatError(error)
      };
    }
  }
  /**
   * Initiate OAuth login with popup
   * Opens OAuth flow in a popup window instead of redirecting
   * 
   * @param {object} provider - Provider configuration from login options
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  static async loginWithOAuthPopup(provider) {
    return new Promise((resolve) => {
      try {
        // Calculate popup position - offset 500px right of center to avoid covering progress indicators
        const popupWidth = 500;
        const popupHeight = 600;
        
        // Calculate center position relative to current window
        const centerX = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
        const centerY = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
        
        // Shift 500px right from center
        const offsetX = centerX + 500;
        
        // Ensure popup stays within screen bounds
        const screenLeft = window.screenX;
        const screenRight = window.screenX + window.screen.availWidth;
        const left = Math.max(screenLeft, Math.min(offsetX, screenRight - popupWidth));
        const top = centerY;
        
        const popupFeatures = `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no`;
        
        // Open popup immediately to avoid popup blockers
        const authWindow = window.open('about:blank', '_blank', popupFeatures);
        
        if (!authWindow) {
          resolve({
            success: false,
            error: 'Please disable popup blockers and try again'
          });
          return;
        }

        // Show loading message in popup
        authWindow.document.write(`
          <html>
            <head><title>Authenticating...</title></head>
            <body style="font-family: Arial, sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #f7fafc;">
              <div style="text-align: center;">
                <div style="width: 40px; height: 40px; border: 4px solid #e2e8f0; border-top-color: #4299e1; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
                <p style="color: #4a5568; margin: 0;">Starting authentication...</p>
              </div>
              <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
            </body>
          </html>
        `);

        // Redirect popup to OAuth URL
        authWindow.location.href = provider.loginUrl;

        // Setup message listener for popup callback
        const messageHandler = (event) => {
          if (event.origin === window.location.origin && event.data.type === 'auth_complete') {
            console.log("Received auth_complete message");
            window.removeEventListener('message', messageHandler);
            clearInterval(checkInterval);
            
            if (authWindow && !authWindow.closed) {
              // debugger;
              authWindow.close();
            }

            if (event.data.success) {
              // Wait a moment for cookies to be set, then get user data
              setTimeout(() => {
                this.getCurrentUser().then(result => {
                  console.log("Our /auth/me endpoint reported", result);
                  if (result.success && result.user) {
                    // Save user to ReturningUserManager with correct userUuid
                    // Only save if user has at least one auth method configured
                    if (result.user.hasPasskey || result.user.hasPassword) {
                      ReturningUserManager.saveUser({
                        userUuid: result.user.userUuid,
                        email: result.user.email,
                        name: result.user.name || result.user.email,
                        hasPasskey: result.user.hasPasskey,
                        hasPassword: result.user.hasPassword
                      });
                      console.log('[OAuth] Saved user to ReturningUserManager:', result.user.userUuid);
                    } else {
                      console.log('[OAuth] User has no password or passkey, skipping ReturningUserManager save:', result.user.userUuid);
                    }
                    
                    resolve({
                      success: true,
                      user: result.user
                    });
                  } else {
                    resolve({
                      success: false,
                      error: 'Not authenticated'
                    });
                  }
                });
              }, 500);
            } else {
              resolve({
                success: false,
                error: event.data.error_description || event.data.error || 'Authentication failed'
              });
            }
          }
        };

        window.addEventListener('message', messageHandler);

        // Monitor popup for closure
        const checkInterval = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkInterval);
            window.removeEventListener('message', messageHandler);
            resolve({
              success: false,
              error: 'Authentication cancelled'
            });
          }
        }, 500);

        // Timeout after 5 minutes
        setTimeout(() => {
          if (authWindow && !authWindow.closed) {
            authWindow.close();
          }
          clearInterval(checkInterval);
          window.removeEventListener('message', messageHandler);
          resolve({
            success: false,
            error: 'Authentication timed out'
          });
        }, 300000);

      } catch (error) {
        console.error('OAuth popup login failed:', error);
        resolve({
          success: false,
          error: this.formatError(error)
        });
      }
    });
  }


  /**
   * Handle OAuth callback
   * Called after user returns from OAuth provider
   * 
   * @param {string} code - Authorization code
   * @param {string} state - State parameter
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  static async handleOAuthCallback(code, state) {
    try {
      const response = await api.post('/auth/oauth/callback', {
        code,
        state
      });
      
      const userData = response.data;
      
      // Save returning user info only if user has at least one auth method configured
      if (userData.hasPasskey || userData.hasPassword) {
        ReturningUserManager.saveUser({
          userUuid: userData.userUuid,
          email: userData.email,
          name: userData.name || userData.email,
          hasPasskey: userData.hasPasskey,
          hasPassword: userData.hasPassword
        });
      }
      
      return { 
        success: true, 
        user: userData
      };
    } catch (error) {
      console.error('OAuth callback failed:', error);
      return { 
        success: false, 
        error: this.formatError(error)
      };
    }
  }

  /**
   * Verify authentication status after OAuth callback
   * Uses /environment endpoint like the working index.html implementation
   *
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  // static async verifyAuthenticationStatus() {
  //   try {
  //     const response = await fetch('/environment', {
  //       credentials: 'include' // Ensure cookies are sent
  //     });
      
  //     if (!response.ok) {
  //       throw new Error('Failed to verify authentication');
  //     }
      
  //     const config = await response.json();
      
  //     // Check if user is authenticated (matching index.html logic)
  //     if (config.authenticated) {
  //       // Get full user data from /auth/me
  //       return await this.getCurrentUser();
  //     } else {
  //       return {
  //         success: false,
  //         error: 'Not authenticated'
  //       };
  //     }
  //   } catch (error) {
  //     console.error('Auth verification error:', error);
  //     return {
  //       success: false,
  //       error: error.message || 'Authentication verification failed'
  //     };
  //   }
  // }

  /**
   * Get current authenticated user
   * Uses existing /auth/me endpoint
   *
   * @param {object} [options]
   * @param {boolean} [options.skipLoading] - Suppress the global page spinner
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  static async getCurrentUser(options = {}) {
    // If a request is already in flight, return that promise instead of making a new one
    if (this._getCurrentUserPromise) {
      return this._getCurrentUserPromise;
    }

    // Create new request and cache the promise
    this._getCurrentUserPromise = (async () => {
      try {
        const response = await api.get('/auth/me', { skipLoading: !!options.skipLoading });

        // Seed session renewal config from the /auth/me payload itself —
        // the backend already merges SessionSecurityConfig server-side and
        // returns it as `sessionConfig`, so the renewal service doesn't
        // need a separate /config/session/{customerId} roundtrip. Falls
        // back to the API fetch only if the field is missing.
        if (response.data && response.data.customerId) {
          try {
            const sessionRenewalService = await getSessionRenewalService();
            await sessionRenewalService.initializeSessionConfig(
              response.data.customerId,
              response.data.sessionConfig,
            );
          } catch (error) {
            console.error('[AuthService] Failed to initialize session config:', error);
            // Don't fail authentication if session config fails to load
          }
        }
        
        return {
          success: true,
          user: response.data
        };
      } catch (error) {
        return {
          success: false,
          error: 'Not authenticated'
        };
      } finally {
        // Clear cache after request completes (success or failure)
        this._getCurrentUserPromise = null;
      }
    })();

    return this._getCurrentUserPromise;
  }

  /**
   * Logout current user
   * Uses existing /auth/tokens/logout endpoint
   * 
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async logout() {
    try {
      await api.post('/auth/tokens/logout');
      // METALARK-835: clear the pending-invitations cache so the next
      // login starts fresh and the navbar badge doesn't carry stale
      // state across users on the same browser.
      try {
        const { store } = await import('./store');
        store.dispatch({ type: 'clearPendingInvitations' });
      } catch (_) {}
      return { success: true };
    } catch (error) {
      console.error('Logout failed:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Check if user is authenticated
   *
   * @returns {Promise<boolean>}
   */
  static async isAuthenticated() {
    const result = await this.getCurrentUser();
    return result.success;
  }

  /**
   * Get current user profile
   * Convenience method that returns user object directly (throws on error)
   *
   * @returns {Promise<object>} User profile object
   * @throws {Error} If not authenticated
   */
  static async getProfile() {
    const result = await this.getCurrentUser();
    if (result.success) {
      return result.user;
    } else {
      throw new Error(result.error || 'Not authenticated');
    }
  }

  /**
   * Request password reset
   * 
   * @param {string} email - User's email
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async forgotPassword(email) {
    try {
      await api.post('/auth/forgot-password', { email });
      return { success: true };
    } catch (error) {
      console.error('Forgot password failed:', error);
      return { 
        success: false, 
        error: this.formatError(error)
      };
    }
  }

  /**
   * Reset password with code
   *
   * @param {string} email - User's email
   * @param {string} code - Reset code
   * @param {string} newPassword - New password
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async resetPassword(email, code, newPassword) {
    try {
      await api.post('/auth/reset-password', {
        email,
        code,
        newPassword
      });
      return { success: true };
    } catch (error) {
      console.error('Password reset failed:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Get current EULA
   *
   * @param {object} [options]
   * @param {boolean} [options.skipLoading] - Suppress the global page spinner
   * @returns {Promise<{success: boolean, eula?: object, error?: string}>}
   */
  static async getEula(options = {}) {
    try {
      const response = await api.get('/eula/current', { skipLoading: !!options.skipLoading });
      return {
        success: true,
        eula: response.data
      };
    } catch (error) {
      console.error('Failed to get EULA:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Fetch the SSO JIT-consent context for a signed token.
   *
   * Public endpoint — does not require an authenticated session, since
   * the user is being asked to confirm provisioning *before* their account
   * is created. The signed token issued by the SSO callback is the
   * authority.
   *
   * @param {string} token - signed consent token from /#sso-consent?t=...
   * @returns {Promise<{success: boolean, context?: object, error?: string}>}
   */
  static async getSsoConsent(token) {
    try {
      const response = await api.get(`/auth/sso/consent?t=${encodeURIComponent(token)}`);
      return {
        success: true,
        context: response.data
      };
    } catch (error) {
      console.error('Failed to fetch SSO consent context:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Confirm SSO JIT consent — atomically creates the User and
   * UserRoleAssignment server-side and mints the session cookie.
   *
   * @param {string} consentToken - signed consent token
   * @returns {Promise<{success: boolean, redirect?: string, error?: string}>}
   */
  static async confirmSsoConsent(consentToken) {
    try {
      const response = await api.post('/auth/sso/consent/confirm', { consentToken });
      return {
        success: !!response.data?.success,
        redirect: response.data?.redirect
      };
    } catch (error) {
      console.error('Failed to confirm SSO consent:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Accept EULA
   *
   * @param {string} version - EULA version to accept
   * @returns {Promise<{success: boolean, error?: string, accountStatus?: string}>}
   */
  static async acceptEula(version) {
    try {
      const response = await api.post('/eula/accept', {
        version
      });
      return {
        success: response.data.success,
        message: response.data.message,
        accountStatus: response.data.accountStatus
      };
    } catch (error) {
      console.error('Failed to accept EULA:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Format error messages for user display
   * @private
   */
  static formatError(error) {
    // Check for axios error response
    if (error.response) {
      const data = error.response.data;
      
      // Handle structured error responses
      if (data && data.message) {
        return data.message;
      }
      
      // Handle common HTTP status codes
      switch (error.response.status) {
        case 401:
          return 'Invalid credentials';
        case 403:
          return 'Access denied';
        case 404:
          return 'User not found';
        case 429:
          return 'Too many attempts. Please try again later';
        case 500:
          return 'Server error. Please try again';
        default:
          return data?.error || 'An error occurred. Please try again';
      }
    }
    
    // Handle network errors
    if (error.request) {
      return 'Network error. Please check your connection';
    }
    
    // Handle other errors
    return error.message || 'An unexpected error occurred';
  }

  /**
   * Check if passkeys are supported in this browser
   *
   * @returns {boolean}
   */
  static isPasskeySupported() {
    return window?.PublicKeyCredential !== undefined &&
           typeof window.PublicKeyCredential === 'function';
  }

  /**
   * Check if conditional mediation (autofill UI) is available
   * This allows passkeys to appear in the browser's autofill suggestions
   *
   * @returns {Promise<boolean>}
   */
  static async isConditionalMediationAvailable() {
    try {
      console.log('[AuthService] Checking conditional mediation availability...');
      
      if (!this.isPasskeySupported()) {
        console.log('[AuthService] Passkeys not supported, conditional mediation unavailable');
        return false;
      }
      
      console.log('[AuthService] Passkeys supported, checking PublicKeyCredential.isConditionalMediationAvailable...');
      
      if (!PublicKeyCredential.isConditionalMediationAvailable) {
        console.log('[AuthService] PublicKeyCredential.isConditionalMediationAvailable method not found');
        console.log('[AuthService] Browser:', navigator.userAgent);
        return false;
      }
      
      const available = await PublicKeyCredential.isConditionalMediationAvailable();
      console.log('[AuthService] Conditional mediation available:', available);
      return available;
    } catch (error) {
      console.error('[AuthService] Error checking conditional mediation:', error);
      return false;
    }
  }

  /**
   * Start conditional passkey authentication (autofill UI)
   * This allows passkeys to be selected from the browser's autofill
   * The promise resolves when user selects a passkey from autofill
   *
   * @param {Function} onSuccess - Callback for successful authentication
   * @param {Function} onError - Callback for errors
   * @returns {Promise<AbortController>} - Controller to abort the operation
   */
  static async startConditionalAuth(onSuccess, onError) {
    try {
      // Check if conditional mediation is available
      const available = await this.isConditionalMediationAvailable();
      if (!available) {
        console.log('[ConditionalAuth] Conditional mediation not available');
        return null;
      }

      console.log('[ConditionalAuth] Starting conditional authentication...');

      // Get authentication challenge (with no email for discoverable credentials)
      const challengeResult = await this.beginPasskeyLogin(null);
      if (!challengeResult.success) {
        onError?.(challengeResult.error);
        return null;
      }

      // Prepare public key options with conditional mediation
      const publicKeyOptions = this.preparePublicKeyOptions(challengeResult.options);

      // Create abort controller so we can cancel if needed
      const abortController = new AbortController();

      // Start conditional authentication in background
      // This will show passkeys in autofill without blocking the UI
      navigator.credentials.get({
        publicKey: publicKeyOptions,
        mediation: 'conditional',
        signal: abortController.signal
      }).then(async (credential) => {
        if (!credential) {
          console.log('[ConditionalAuth] No credential selected');
          return;
        }

        console.log('[ConditionalAuth] Credential selected from autofill');

        // Prepare verification payload
        const verifyPayload = {
          credential: {
            id: credential.id,
            rawId: this.arrayBufferToBase64(credential.rawId),
            response: {
              authenticatorData: this.arrayBufferToBase64(credential.response.authenticatorData),
              clientDataJSON: this.arrayBufferToBase64(credential.response.clientDataJSON),
              signature: this.arrayBufferToBase64(credential.response.signature),
              userHandle: credential.response.userHandle ? this.arrayBufferToBase64(credential.response.userHandle) : null
            },
            type: credential.type
          }
        };

        // Send credential to backend for verification
        try {
          const response = await api.post('/auth/passkey/login/verify', verifyPayload);
          
          // API returns {success: true, user: {...}}
          const result = response.data;
          const userData = result.user;
          
          // Save returning user info only if user has at least one auth method configured
          if (userData.hasPasskey || userData.hasPassword) {
            ReturningUserManager.saveUser({
              userUuid: userData.userUuid,
              email: userData.email,
              name: userData.name || userData.email,
              hasPasskey: userData.hasPasskey,
              hasPassword: userData.hasPassword
            });
          }
          
          onSuccess?.({
            success: true,
            user: userData
          });
        } catch (error) {
          console.error('[ConditionalAuth] Verification failed:', error);
          
          // Preserve error code and data for special handling (e.g., EULA required)
          const errorCode = error.response?.data?.error;
          const errorMessage = error.response?.data?.message || this.formatError(error);
          
          // Pass error object with code and message
          onError?.({
            message: errorMessage,
            errorCode: errorCode,
            response: error.response
          });
        }
      }).catch((error) => {
        // Ignore abort errors (user cancelled)
        if (error.name === 'AbortError') {
          console.log('[ConditionalAuth] Conditional auth aborted');
          return;
        }
        
        console.error('[ConditionalAuth] Error:', error);
        onError?.(this.formatError(error));
      });

      return abortController;
    } catch (error) {
      console.error('[ConditionalAuth] Failed to start:', error);
      onError?.(this.formatError(error));
      return null;
    }
  }

  /**
   * Convert ArrayBuffer to Base64url string (URL-safe, no padding)
   * WebAuthn requires base64url encoding for all binary data
   * @private
   */
  static arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    // Convert to standard base64, then to base64url
    const base64 = btoa(binary);
    // Replace + with -, / with _, and remove padding =
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /**
   * Convert Base64 or Base64url string to ArrayBuffer
   * Handles both standard base64 and base64url formats
   * @private
   */
  static base64ToArrayBuffer(base64) {
    // Convert base64url to standard base64 if needed
    // base64url uses - and _ instead of + and /, and omits padding
    let standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const padding = standardBase64.length % 4;
    if (padding) {
      standardBase64 += '='.repeat(4 - padding);
    }
    
    const binary = atob(standardBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Prepare public key options for WebAuthn API
   * Converts base64 strings to ArrayBuffers
   * @private
   */
  static preparePublicKeyOptions(options) {
    const prepared = {
      ...options,
      challenge: this.base64ToArrayBuffer(options.challenge)
    };

    // Convert allowCredentials if present
    if (options.allowCredentials && options.allowCredentials.length > 0) {
      prepared.allowCredentials = options.allowCredentials.map(cred => ({
        ...cred,
        id: this.base64ToArrayBuffer(cred.id)
      }));
    }

    return prepared;
   }
}

/**
 * Convenience exports for backward compatibility
 * These allow consuming code to import from either location
 */
export async function getCurrentUser() {
  const result = await AuthService.getCurrentUser();
  if (result.success) {
    return result.user;
  } else {
    throw new Error(result.error);
  }
}

export async function logout() {
  const result = await AuthService.logout();
  if (!result.success) {
    throw new Error(result.error);
  }
  return result;
}

export async function getProfile() {
  return getCurrentUser();
}

export const authService = {
  getCurrentUser,
  getProfile,
  logout,
  ...AuthService
};